import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { apiClient } from '../../lib/api'
import { Button } from '../ui/button'
import { Card } from '../ui/card'
import { Plus, BookOpen, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { getLanguageByCode, getLanguageFlag } from '../../lib/constants/languages'
import BookCard from '../BookCard'

interface GradedTranslation {
  translation_id: string
  language: string
  cefr_level?: string | null
  status: string
  file_format: string
  book_id: string
  title_international: string
  author_international: string
  cover_url?: string | null
  original_language?: string | null
  estimated_pages?: number | null
  reading_progress: number
  created_at: string
}

interface GradedBooksSectionProps {
  onBooksLoaded?: (count: number) => void
}

// Language and level badges row (similar to TakenBooksSection)
function CoverBadges({ language, level, status }: { language: string; level?: string | null; status: string }) {
  return (
    <div className="cover-badges flex items-center gap-1">
      <span className="language-flag text-sm" title={language}>
        {getLanguageFlag(language)}
      </span>
      {level && (
        <span className="cefr-badge bg-primary text-primary-foreground text-[10px] px-1.5 py-0.5 rounded font-medium">
          {level}
        </span>
      )}
      {status === 'processing' && (
        <span className="status-badge bg-yellow-500 text-white text-[10px] px-1.5 py-0.5 rounded font-medium animate-pulse">
          ...
        </span>
      )}
    </div>
  )
}

// Reading progress bar
function ProgressBar({ progress }: { progress: number }) {
  if (progress <= 0) return null
  return (
    <div className="progress-bar-container w-full h-1.5 bg-black/40 rounded-b-sm overflow-hidden">
      <div 
        className="progress-bar-fill h-full bg-primary transition-all duration-300"
        style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
      />
    </div>
  )
}

export function GradedBooksSection({ onBooksLoaded }: GradedBooksSectionProps) {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const [translations, setTranslations] = useState<GradedTranslation[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadTranslations()
  }, [])

  const loadTranslations = async () => {
    try {
      setLoading(true)
      const response = await apiClient.reader.getGradedTranslations()
      setTranslations(response.translations || [])
      onBooksLoaded?.(response.count || 0)
    } catch (error) {
      console.error('Error loading graded translations:', error)
    } finally {
      setLoading(false)
    }
  }

  const getLanguageDisplayName = (langCode: string): string => {
    const lang = getLanguageByCode(langCode)
    return lang ? lang.name : langCode.toUpperCase()
  }

  const formatPages = (pages?: number | null): string => {
    if (!pages) return ''
    return `${pages} ${t('dashboard.pages', 'p.')}`
  }

  const handleReadOnline = (trans: GradedTranslation, e: React.MouseEvent) => {
    e.stopPropagation()
    if (trans.status === 'available' && trans.file_format.toLowerCase() === 'epub') {
      navigate(`/read/${trans.book_id}/${trans.translation_id}`, { state: { from: 'my-books' } })
    }
  }

  if (loading) {
    return (
      <div className="graded-books-section mb-8">
        <div className="section-header flex items-center gap-3 mb-4">
          <div className="w-2 h-6 bg-primary rounded-full"></div>
          <h2 className="text-xl font-semibold">{t('dashboard.myGradedBooks', 'My Graded Books')}</h2>
        </div>
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </div>
    )
  }

  return (
    <div className="graded-books-section mb-8">
      <div className="section-header flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-2 h-6 bg-primary rounded-full"></div>
          <div>
            <h2 className="text-xl font-semibold">{t('dashboard.myGradedBooks', 'My Graded Books')}</h2>
            <p className="text-sm text-muted-foreground">
              {t('dashboard.gradedBooksDescription', 'Books you\'ve uploaded and translated')}
            </p>
          </div>
        </div>
        <Button 
          onClick={() => navigate('/user/books/new')}
          size="sm"
          className="flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          {t('dashboard.addBook', 'Add Book')}
        </Button>
      </div>

      {translations.length === 0 ? (
        <Card className="graded-books-empty p-8 text-center">
          <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
            <BookOpen className="h-8 w-8 text-muted-foreground" />
          </div>
          <h3 className="text-lg font-medium mb-2">
            {t('dashboard.noGradedBooks', 'No graded books yet')}
          </h3>
          <p className="text-muted-foreground mb-4 max-w-md mx-auto">
            {t('dashboard.noGradedBooksDescription', 'Upload your own books and translate them to your preferred language and level.')}
          </p>
          <Button onClick={() => navigate('/user/books/new')}>
            <Plus className="h-4 w-4 mr-2" />
            {t('dashboard.uploadFirstBook', 'Upload Your First Book')}
          </Button>
        </Card>
      ) : (
        <div className="graded-books-grid grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-6">
          {translations.map((trans) => (
            <BookCard
              key={trans.translation_id}
              book={{
                id: trans.book_id,
                title_international: trans.title_international,
                author_international: trans.author_international,
                cover_url: trans.cover_url || undefined,
                available_languages: [trans.language],
                genre: null,
                original_language: trans.original_language || ''
              }}
              variant="user-book"
              onCardClick={() => navigate(`/user/books/${trans.book_id}/translations/${trans.translation_id}`)}
              badges={
                <CoverBadges 
                  language={trans.language} 
                  level={trans.cefr_level}
                  status={trans.status}
                />
              }
              bottomOverlay={
                <ProgressBar progress={trans.reading_progress} />
              }
              extraInfo={
                <div className="extra-info-row flex items-center gap-2 text-xs text-muted-foreground flex-wrap">
                  <span>{getLanguageDisplayName(trans.language)}</span>
                  {trans.cefr_level && (
                    <>
                      <span className="text-muted-foreground/50">•</span>
                      <span>{trans.cefr_level}</span>
                    </>
                  )}
                  {trans.estimated_pages && (
                    <>
                      <span className="text-muted-foreground/50">•</span>
                      <span>{formatPages(trans.estimated_pages)}</span>
                    </>
                  )}
                </div>
              }
              onHoverActionClick={trans.status === 'available' && trans.file_format.toLowerCase() === 'epub' 
                ? (e) => handleReadOnline(trans, e) 
                : undefined
              }
              hoverActionLabel={trans.status === 'available' ? t('reader.read', 'Read') : undefined}
              showLanguageFlags={false}
              showGenre={false}
            />
          ))}
        </div>
      )}
    </div>
  )
}
